import React from 'react';
import { useTheme } from 'styled-components';
import { Icon, SectionHeading } from '@tviklax/ui-components';
import { VesselScheduleProps } from './SchedulingContextTypes';
import {
  VesselScheduleContainer,
  VesselGrid,
  VesselCard,
  VesselName,
  VesselInfo,
  InfoRow,
  InfoLabel,
  InfoValue,
  RecommendationBadge,
} from './SchedulingContextStyles';

export const VesselSchedule: React.FC<VesselScheduleProps> = ({
  stageIndex,
  vessels = [],
  promisedDate,
  isLoading = false,
}) => {
  const theme = useTheme();
  const shouldShow = stageIndex >= 4;

  if (!shouldShow) {
    return null;
  }

  if (isLoading) {
    return (
      <VesselScheduleContainer>
        <SectionHeading icon="Ship">Vessel Schedule</SectionHeading>
        <div style={{ color: theme.colors.textMuted, fontSize: '14px' }}>Loading vessels...</div>
      </VesselScheduleContainer>
    );
  }

  if (vessels.length === 0) {
    return (
      <VesselScheduleContainer>
        <SectionHeading icon="Ship">Vessel Schedule</SectionHeading>
        <div style={{ color: theme.colors.textMuted, fontSize: '14px' }}>
          No vessels available at this time. Check back soon.
        </div>
      </VesselScheduleContainer>
    );
  }

  const promisedDateObj = new Date(promisedDate);

  return (
    <VesselScheduleContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.35 }}
    >
      <SectionHeading icon="Ship">Available Vessels - Schedule</SectionHeading>
      <VesselGrid>
        {vessels.map((vessel, index) => (
          <VesselCard key={`${vessel.name}-${index}`} $isRecommended={vessel.recommendation}>
            <VesselName>{vessel.name}</VesselName>
            <VesselInfo>
              <InfoRow>
                <InfoLabel>ETA</InfoLabel>
                <InfoValue>
                  {new Date(vessel.eta).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  })}
                </InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Promised</InfoLabel>
                <InfoValue>
                  {promisedDateObj.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  })}
                </InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Status</InfoLabel>
                <InfoValue>
                  {new Date(vessel.eta) <= promisedDateObj ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: theme.colors.success }}>
                      <Icon name="CheckCircle" size={13} /> On Time
                    </span>
                  ) : (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: theme.colors.warning }}>
                      <Icon name="AlertTriangle" size={13} /> Late
                    </span>
                  )}
                </InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Cost</InfoLabel>
                <InfoValue>${vessel.cost.toLocaleString()}</InfoValue>
              </InfoRow>
              {vessel.capacity && (
                <InfoRow>
                  <InfoLabel>Capacity</InfoLabel>
                  <InfoValue>{vessel.capacity}</InfoValue>
                </InfoRow>
              )}
              {vessel.containerCount && (
                <InfoRow>
                  <InfoLabel>Containers</InfoLabel>
                  <InfoValue>{vessel.containerCount}</InfoValue>
                </InfoRow>
              )}
            </VesselInfo>
            {vessel.recommendation && <RecommendationBadge>Recommended</RecommendationBadge>}
          </VesselCard>
        ))}
      </VesselGrid>
    </VesselScheduleContainer>
  );
};
